<template>
  <div class="wheatstone-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Rx = (R₂/R₁) · R₃$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>桥路电流 Ig (灵敏度 10x)</label>
            <span class="val" :style="{color: isBalanced ? '#10b981' : '#f43f5e'}">
               {{ (balanceOffset * 5).toFixed(1) }} <small>μA</small>
            </span>
          </div>
        </div>

        <svg width="100%" height="250" viewBox="0 0 400 250" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-bal"><feGaussianBlur stdDeviation="4" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-e" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#64748b" /></marker>
          </defs>

          <!-- 桥路菱形导线 -->
          <g transform="translate(200, 110)" stroke-width="3" fill="none">
             <!-- 电阻支路连接 -->
             <path d="M -80,0 L 0,-80 L 80,0 M -80,0 L 0,80 L 80,0" stroke="#1e293b" />
             
             <!-- R1, R2, R3, Rx 标识 -->
             <g transform="translate(-40,-40) rotate(-45)"><rect x="-12" y="-5" width="24" height="10" fill="#0f172a" stroke="#64748b" rx="2"/><text y="-10" text-anchor="middle" fill="#64748b" font-size="9">R₁</text></g>
             <g transform="translate(40,-40) rotate(45)"><rect x="-12" y="-5" width="24" height="10" fill="#0f172a" stroke="#64748b" rx="2"/><text y="-10" text-anchor="middle" fill="#64748b" font-size="9">R₂</text></g>
             <g transform="translate(-40,40) rotate(45)"><rect x="-12" y="-5" width="24" height="10" fill="#0f172a" stroke="#64748b" rx="2"/><text y="18" text-anchor="middle" fill="#64748b" font-size="9">R₃</text></g>
             <g transform="translate(40,40) rotate(-45)"><rect x="-12" y="-5" width="24" height="10" fill="#0f172a" stroke="#f43f5e" stroke-width="2" rx="2"/><text y="18" text-anchor="middle" fill="#f43f5e" font-size="9" font-weight="900">Rx</text></g>

             <!-- 中心检流计 (Galvanometer) -->
             <line x1="0" y1="-80" x2="0" y2="80" stroke="#334155" stroke-dasharray="2,2" />
             <g transform="translate(0,0)">
                <circle r="30" fill="#020617" stroke="#334155" stroke-width="2" />
                <path d="M -20,-10 A 20,20 0 0 1 20,-10" fill="none" stroke="#1e293b" stroke-width="1.5" />
                <!-- 指针: 根据平衡度动态偏转 -->
                <line x1="0" y1="0" :x2="22 * Math.sin(smoothAngle)" :y2="-22 * Math.cos(smoothAngle)" 
                      :stroke="isBalanced ? '#10b981' : '#f43f5e'" stroke-width="3" stroke-linecap="round" 
                      :filter="isBalanced ? 'url(#glow-bal)' : ''" />
                <circle r="4" fill="#1e293b" stroke="currentColor" />
             </g>
          </g>

          <!-- 外部电源 E -->
          <path d="M 60,110 H 120 M 280,110 H 340" stroke="#1e293b" stroke-width="2" />
          <rect x="50" y="100" width="10" height="20" fill="#64748b" />
          <text x="55" y="135" text-anchor="middle" fill="#64748b" font-size="9">E (DC)</text>
        </svg>

        <div class="feedback-hint" :class="{ balanced: isBalanced }">
           {{ isBalanced ? '【电桥平衡】 Ig ≈ 0 | Rx 测量完成！' : '指针偏转：调整 Rx 以平衡电位' }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">微调 Rx 电阻值 (Ω)</label>
                 <el-slider v-model="rxVal" :min="20" :max="80" :step="0.1" size="small"></el-slider>
              </div>
              <div class="balance-calc">
                 <div class="calc-row"><small>当前比值:</small> R₂/R₁ = 1.0</div>
                 <div class="calc-row"><strong>平衡点:</strong> 50.0 Ω</div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-odometer"></i>
           <p><strong>核心揭示：</strong>惠斯通电桥利用“比对平衡”消除误差。当你微调待测电阻时，指针的归零意味着菱形两端的电位相等。由于指针在零点由于极小的电位差就会产生明显的摆动，这种<strong>零位法</strong>的灵敏度远高于直接用万用表读取数值。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'WheatstoneBridgeViz',
  data() {
    return {
      rxVal: 40,
      r1: 50,
      r2: 50,
      r3: 50,
      smoothAngle: 0
    }
  },
  computed: {
    balanceOffset() { return this.rxVal - 50; },
    isBalanced() { return Math.abs(this.balanceOffset) < 0.2; }
  },
  watch: {
    balanceOffset: {
      immediate: true,
      handler(val) {
         // Apply a non-linear sensitivity and simple damping
         const target = Math.atan(val * 0.15); // Use atan to constrain and provide high sensitivity near zero
         this.updateAngle(target);
      }
    }
  },
  methods: {
    updateAngle(target) {
       // Simple damping for the pointer movement
       const step = () => {
          const diff = target - this.smoothAngle;
          this.smoothAngle += diff * 0.15;
          if (Math.abs(diff) > 0.001) {
             this.animId = requestAnimationFrame(step);
          }
       };
       cancelAnimationFrame(this.animId);
       step();
    }
  },
  beforeDestroy() {
    if (this.animId) cancelAnimationFrame(this.animId);
  }
}
</script>

<style scoped lang="scss">
.wheatstone-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 250px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #10b981; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; } } }
.feedback-hint { position: absolute; bottom: 12px; width: 100%; text-align: center; color: #475569; font-size: 11px; font-weight: bold; &.balanced { color: #10b981; text-shadow: 0 0 8px rgba(16,185,129,0.3); } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.balance-calc { text-align: right; color: #475569; line-height: 1.2; small { font-size: 9px; } strong { font-size: 12px; color: #10b981; } }
.flex { display: flex; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; } .items-center { align-items: center; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
